import { Card, CardContent, CardHeader, CircularProgress, Stack, Typography } from "@mui/material";
import { LinearGauge } from "devextreme-react";
import {
  MinorTick,
  Range,
  RangeContainer,
  Scale,
  Tooltip,
  ValueIndicator
} from "devextreme-react/linear-gauge";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectPeriod } from "../../../helpers/calendarFunctions";
import { formatNumberCustom } from "../../../helpers/functions";
import { useGetBuildingByIdQuery } from "../../../redux//metaDataApi";
import { useCurrencySymbol } from "../../../redux/customHooks/customHooks";
import { useGetInterventionsMeasurementQuery } from "../../../redux/dataApi";
import { changeCalendarSelection } from "../../../redux/slices/calendarSlice";
import { LoadingOverlay } from "../../Costs/TotalSavings/TotalSavings.styled";
import { StyledNumbers } from "../../Utilization/Utilization.styled";

const calculateScale = (value) => {
  if (value === 0) {
    return { startValue: 0, endValue: 1, tickInterval: 0.2 };
  }
  const magnitude = Math.floor(Math.log10(value));
  const endValue = Math.pow(10, magnitude + 1);
  const tickInterval = endValue / 5;

  return { startValue: 0, endValue, tickInterval };
};

const GhostHoursOptimization = () => {
  const dispatch = useDispatch();
  const currency = useCurrencySymbol();

  const tenantId = useSelector((state) => state.session.selectedTenant.id);
  const selectedBuilding = useSelector((state) => state.buildings.selectedBuilding);
  const calendarSelection = useSelector((state) => state.calendar.calendarSelection);

  const { data: building, isFetching: isFetchingBuilding } = useGetBuildingByIdQuery(
    selectedBuilding.id
  );

  const startDate = calendarSelection?.dateStart;
  const endDate = calendarSelection?.dateEnd;

  const shouldSkipQuery = !tenantId || !selectedBuilding.id || !startDate || !endDate;

  const baseQueryParams = {
    tenantId,
    locationId: selectedBuilding.id,
    queryParams: {
      dateStart: startDate,
      dateEnd: endDate,
      measurementType: "Sum"
    }
  };

  const { data: coolingOptimizationData, isFetching: isLoadingCoolingOptimizationData } =
    useGetInterventionsMeasurementQuery(
      {
        ...baseQueryParams,
        queryParams: { ...baseQueryParams.queryParams, interventionType: "CoolingOptimization" }
      },
      { skip: shouldSkipQuery }
    );

  const { data: heatingOptimizationData, isFetching: isFetchingHeatingOptimizationData } =
    useGetInterventionsMeasurementQuery(
      {
        ...baseQueryParams,
        queryParams: { ...baseQueryParams.queryParams, interventionType: "HeatingOptimization" }
      },
      { skip: shouldSkipQuery }
    );

  useEffect(() => {
    if (selectedBuilding) {
      if (calendarSelection.selectedPeriod === "Current") {
        selectPeriod("Day", dispatch);
        dispatch(changeCalendarSelection());
      }
    }
  }, [tenantId, selectedBuilding, calendarSelection, dispatch]);

  const totalGhostHoursSaved =
    (coolingOptimizationData?.sum || 0) + (heatingOptimizationData?.sum || 0);

  const scale = calculateScale(totalGhostHoursSaved);

  const isLoading =
    isLoadingCoolingOptimizationData || isFetchingHeatingOptimizationData || isFetchingBuilding;

  return (
    <Card sx={{ padding: 4, borderRadius: 4 }}>
      {/* TODO: Implement translations in this component */}
      <CardHeader title="Ghost hours optimization" sx={{ textAlign: "center" }} />
      {isLoading ? (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      ) : (
        <CardContent sx={{ display: "flex", justifyContent: "center" }}>
          <Stack direction="column">
            <Stack
              direction="row"
              spacing={4}
              display={"flex"}
              justifyContent={"space-evenly"}
              alignItems={"center"}
            >
              <Stack direction="column">
                <StyledNumbers>
                  {formatNumberCustom(totalGhostHoursSaved, false, 2, 4)} hr
                </StyledNumbers>
                <Typography variant="p">total hours saved</Typography>
              </Stack>
              <Stack direction="column">
                <StyledNumbers>
                  {currency}
                  {formatNumberCustom(
                    totalGhostHoursSaved * (building?.benchmarkEnergyCost || 0),
                    false,
                    2,
                    4
                  )}
                </StyledNumbers>
                <Typography variant="p">potential savings</Typography>
              </Stack>
            </Stack>
            <LinearGauge style={{ width: "400px" }} id="linearGauge" value={totalGhostHoursSaved}>
              <Tooltip
                enabled={true}
                customizeTooltip={(args) => {
                  return {
                    text: `${formatNumberCustom(args.value, false, 2, 4)} hr`
                  };
                }}
              />
              <Scale
                startValue={scale.startValue}
                endValue={scale.endValue}
                tickInterval={scale.tickInterval}
              >
                <MinorTick visible={true} />
              </Scale>
              <ValueIndicator type="rangeBar" color="rgba(55, 180, 0, 0.7)" size={28} />
              <RangeContainer offset={10} palette={"soft"} width={28}>
                <Range startValue={0} endValue={scale.endValue} color="rgba(55, 180, 0, 0.2)" />
              </RangeContainer>
            </LinearGauge>
            <Typography variant="p" sx={{ alignSelf: "flex-start" }}>
              Ghost hours saved
            </Typography>
          </Stack>
        </CardContent>
      )}
    </Card>
  );
};

export default GhostHoursOptimization;
